import { ApiClient, type AuthSessionResponse } from '@ignite/api-client';

export const tokenKey = 'ignite.auth.token';
export const userKey = 'ignite.auth.user';
export type StoredUser = {
  id?: string;
  email: string;
  display_name?: string;
};

let unauthorizedRedirecting = false;

export const apiClient = new ApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787',
  getToken: () => localStorage.getItem(tokenKey),
  onUnauthorized: () => {
    clearStoredSession();
    redirectToLogin();
  },
});

export function persistSession(result: AuthSessionResponse) {
  unauthorizedRedirecting = false;
  localStorage.setItem(tokenKey, result.token);
  localStorage.setItem(userKey, JSON.stringify(result.user));
}

export function clearStoredSession() {
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(userKey);
}

function redirectToLogin() {
  if (unauthorizedRedirecting) return;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current.startsWith('/login') || current.startsWith('/auth/callback')) return;

  unauthorizedRedirecting = true;
  const loginUrl = new URL('/login', window.location.origin);
  loginUrl.searchParams.set('return_to', current);
  window.location.assign(`${loginUrl.pathname}${loginUrl.search}`);
}

export function readStoredUser(): StoredUser | null {
  const raw = localStorage.getItem(userKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredUser>;
    return typeof parsed.email === 'string' ? { ...parsed, email: parsed.email } : null;
  } catch {
    return null;
  }
}

export function userFromJwt(token: string): StoredUser | null {
  // 仅用于本地引导状态分桶；鉴权仍由 Worker 校验签名后的 bearer token。
  const claims = decodeJwtPayload(token);
  if (!claims) return null;

  const id = typeof claims.sub === 'string' ? claims.sub : undefined;
  const email = typeof claims.email === 'string' ? claims.email : null;
  if (!id && !email) return null;
  return { id, email: email ?? 'Google OAuth' };
}

export function isStoredTokenValid(token: string, now = Date.now(), skewMs = 30_000): boolean {
  const claims = decodeJwtPayload(token);
  return (
    claims !== null &&
    typeof claims.exp === 'number' &&
    Number.isFinite(claims.exp) &&
    claims.exp * 1000 > now + skewMs
  );
}

export function readStoredSessionIdentity(): { userId: string; tenantId: string } | null {
  const token = localStorage.getItem(tokenKey);
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  return typeof claims?.sub === 'string' && typeof claims.tenant_id === 'string'
    ? { userId: claims.sub, tenantId: claims.tenant_id }
    : null;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}
